import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import PrintingHeader from "../../components/printingHeader/PrintingHeader";
import { useReactToPrint } from "react-to-print";
import MobileNav from "../../components/mobileNav/MobileNav";
import printIcon from "../../assets/print-icon.svg";
import "./ratingsReport.css";
import { Helmet } from "react-helmet";
import ReactPaginate from "react-paginate";

const RatingsReport = () => {
  const [ratings, setRatings] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStar, setSelectedStar] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTechnician, setSelectedTechnician] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const itemsPerPageOptions = [25, 50, 100, 300, 400, totalItems];

  const fetchRatings = useCallback(
    async (currentPage = 1, pageSize = itemsPerPage) => {
      try {
        setLoading(true);
        const query = new URLSearchParams();

        // Parse name and phone
        const parts = searchQuery.trim().split(" ");
        const isPhone = /^\d+$/;
        let name = "";
        let phone = "";

        parts.forEach((part) => {
          if (isPhone.test(part)) {
            phone = part;
          } else {
            name += `${part} `;
          }
        });

        if (name.trim()) query.append("CustomerName", name.trim());
        if (phone) query.append("CustomerPhone", phone);
        if (selectedTechnician)
          query.append("TechnicianName", selectedTechnician);
        if (selectedStar) query.append("RatingValue", selectedStar);

        // Append MaintenanceAreaIds
        const maintenanceAreaIdsString =
          localStorage.getItem("MaintenanceAreaIds");
        const maintenanceAreaIds = maintenanceAreaIdsString
          ? maintenanceAreaIdsString.split(",").map(Number)
          : [];

        maintenanceAreaIds.forEach((id) => {
          query.append("MaintenanceAreaIds", id);
        });

        // Add pagination parameters
        query.append("Page", currentPage);
        query.append("PageSize", pageSize);

        const response = await axios.post(
          `https://api.generalhome.sa/GetRatings?${query.toString()}`
        );

        // ============================
        //  FIX OLD IMAGE URL HERE
        // ============================

        const oldDomain = "https://aircon-api.hiveagency.tech";
        const newDomain = "https://api.generalhome.sa";

        const fixedItems = response.data.Items.map((item) => {
          return {
            ...item,
            photo: item.photo?.startsWith(oldDomain)
              ? item.photo.replace(oldDomain, newDomain)
              : item.photo,

            photo2: item.photo2?.startsWith(oldDomain)
              ? item.photo2.replace(oldDomain, newDomain)
              : item.photo2,
          };
        });

        // Pagination handling
        const totalItems = response.data.TotalItems || response.data.length;
        const totalPages = response.data.TotalPages || 1;
        const totalNumberOfPages =
          totalPages || Math.ceil(totalItems / pageSize);

        setPageCount(totalNumberOfPages);
        setRatings(fixedItems);
        setTotalItems(totalItems);
        setCurrentPage(currentPage - 1);
      } catch (err) {
        setError(err.message || "حدث خطأ أثناء جلب البيانات");
        console.error("Error fetching ratings:", err);
        setRatings([]);
        setTotalItems(0);
        setPageCount(0);
        setCurrentPage(0);
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    },
    [searchQuery, selectedTechnician, selectedStar, itemsPerPage]
  );

  const getApprovedTechnicians = useCallback(async () => {
    try {
      const ids = localStorage.getItem("MaintenanceAreaIds");
      const maintenanceAreaIds = ids?.split(",").map(Number);
      const url = `https://api.generalhome.sa/api/maintenance/GetApprovedTechnicians`;
      const res = await axios.post(url, {
        maintenanceAreaIds: maintenanceAreaIds || [],
      });
      setTechnicians(res.data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching technicians:", err);
    }
  }, []);

  // Initial data load - parallel requests
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setInitialLoad(true);
        await Promise.all([
          getApprovedTechnicians(),
          fetchRatings(1, itemsPerPage),
        ]);
      } catch (err) {
        setError(err.message || "حدث خطأ أثناء جلب البيانات");
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    };
    loadData();
  }, [getApprovedTechnicians]);

  // Fetch ratings when filters change
  useEffect(() => {
    fetchRatings(1, itemsPerPage);
  }, [
    searchQuery,
    selectedTechnician,
    selectedStar,
    itemsPerPage,
    fetchRatings,
  ]);

  const handleSearchIconClick = () => {
    setLoading(true);
    fetchRatings(1, itemsPerPage);
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedStar("");
    setSelectedTechnician("");
  };

  // Pagination click handler
  const handlePageClick = (data) => {
    const selectedPage = data.selected;
    const pageNumber = selectedPage + 1;
    fetchRatings(pageNumber, itemsPerPage);
  };

  // Items per page change handler
  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = parseInt(event.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(0);
    fetchRatings(1, newItemsPerPage);
  };

  // Function to open image in popup
  const openImagePopup = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  // Function to close image popup
  const closeImagePopup = () => {
    setSelectedImage(null);
  };

  const renderStars = (value) => {
    return (
      <div className="stars-container">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="34"
          height="35"
          viewBox="0 0 34 35"
          fill="none"
        >
          <path
            d="M15.6622 3.78104C16.0860 2.83371 16.2981 2.36005 16.5932 2.21419C16.8496 2.08746 17.1506 2.08746 17.4069 2.21419C17.7021 2.36005 17.9141 2.83371 18.338 3.78104L21.7143 11.3266C21.8397 11.6067 21.9023 11.7467 21.9993 11.8539C22.0850 11.9488 22.1899 11.0247 22.3069 11.0767C22.4393 11.1354 22.5924 11.1515 22.8985 11.1837L31.1458 13.0508C32.1812 13.1596 32.6988 13.2141 32.9293 13.4486C33.1294 13.6523 33.2225 13.9376 33.1807 14.2197C33.1327 14.5443 32.7460 14.8914 31.9726 15.5858L25.812 21.1164C25.5835 21.3216 25.4690 21.4243 25.3967 21.5492C25.3326 21.66 25.2925 21.7827 25.2792 21.9097C25.2640 22.0532 25.2958 22.2031 25.3597 22.5032L27.0805 30.5846C27.2965 31.5993 27.4046 32.1065 27.2519 32.3973C27.1191 32.6501 26.8756 32.8262 26.5934 32.8739C26.2686 32.9286 25.8177 32.6696 24.9157 32.1514L17.7319 28.0238C17.4653 27.8705 17.3320 27.7941 17.1903 27.764C17.0649 27.7376 16.9352 27.7376 16.8098 27.764C16.6681 27.7941 16.5348 27.8705 16.2682 28.0238L9.08445 32.1514C8.18255 32.6696 7.73160 32.9286 7.40677 32.8739C7.12460 32.8262 6.88100 32.6501 6.74837 32.3973C6.59568 32.1065 6.70369 31.5993 6.91973 30.5846L8.64040 22.5032C8.70427 22.2031 8.73620 22.0532 8.72104 21.9097C8.70761 21.7827 8.66757 21.66 8.60349 21.5492C8.53107 21.4243 8.41675 21.3216 8.18811 21.1164L2.02759 15.5858C1.25417 14.8914 0.867455 14.5443 0.819373 14.2197C0.777627 13.9376 0.870659 13.6523 1.07086 13.4486C1.30134 13.2141 1.81905 13.1596 2.85446 13.0508L11.1017 12.1837C11.4078 12.1515 11.5608 12.1354 11.6932 12.0767C11.8102 12.0247 11.9151 11.9488 12.0009 11.8539C12.0978 11.7467 12.1605 11.6067 12.2858 11.3266L15.6622 3.78104Z"
            fill="#FFCB45"
          />
        </svg>
        {value}.0
      </div>
    );
  };

  // Render pagination component
  const renderPagination = () => (
    <ReactPaginate
      forcePage={currentPage}
      previousLabel={
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6.94 4L6 4.94L9.05333 8L6 11.06L6.94 12L10.94 8L6.94 4Z"
            fill="black"
          />
        </svg>
      }
      nextLabel={
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.06 12L11 11.06L7.94667 8L11 4.94L10.06 4L6.06 8L10.06 12Z"
            fill="black"
            fillOpacity="0.3"
          />
        </svg>
      }
      breakLabel={"..."}
      pageCount={pageCount}
      marginPagesDisplayed={2}
      pageRangeDisplayed={3}
      onPageChange={handlePageClick}
      containerClassName={"pagination justify-content-end"}
      pageClassName={"page-item"}
      pageLinkClassName={"page-link"}
      previousClassName={"page-item"}
      previousLinkClassName={"page-link"}
      nextClassName={"page-item"}
      nextLinkClassName={"page-link"}
      breakClassName={"page-item"}
      breakLinkClassName={"break-link"}
      activeClassName={"active"}
    />
  );

  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: "Report",
    onAfterPrint: () => console.log("print success"),
  });

  if (error) {
    return (
      <div className="error-container">
        <p>خطأ: {error}</p>
        <button
          onClick={() => window.location.reload()}
          className="retry-button"
        >
          إعادة المحاولة
        </button>
      </div>
    );
  }

  const isValidImageUrl = (url) => {
    if (!url) return false;

    try {
      const parsed = new URL(url);

      // Must have a file extension (jpg, png, jpeg, webp, etc.)
      return /\.(jpg|jpeg|png|webp|gif)$/i.test(parsed.pathname);
    } catch {
      return false;
    }
  };

  return (
    <>
      <MobileNav />
      <section className="ratingsReport" ref={componentRef}>
        <Helmet>
          <meta charSet="utf-8" />
          <title>التقييمات</title>
          <link
            rel="canonical"
            href="https://generalhome.sa/dashboard/ratingsReport"
          />
        </Helmet>
        <div className="ratingsReport-container container">
          <PrintingHeader title="التقييمات" count={totalItems} />

          {/* Image Popup Modal */}
          {selectedImage && (
            <div className="image-popup-overlay" onClick={closeImagePopup}>
              <div
                className="image-popup-content"
                onClick={(e) => e.stopPropagation()}
              >
                <button className="close-popup-btn" onClick={closeImagePopup}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                  >
                    <path
                      d="M1 1L13 13M13 1L1 13"
                      stroke="#C4002A"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
                <img
                  src={selectedImage}
                  alt="Enlarged view"
                  className="popup-image"
                />
              </div>
            </div>
          )}

          {/* Search Bar */}
          <div className="ratingsReport-search">
            <div className="input-box">
              <input
                type="text"
                placeholder="ابحث عن اسم العميل أو رقم الجوال"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearchIconClick();
                }}
              />
            </div>
            <div className="search-icon" onClick={handleSearchIconClick}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="42"
                height="42"
                viewBox="0 0 42 42"
                fill="none"
              >
                <path
                  d="M28.7875 28.7353L36 36M32.6667 19.3333C32.6667 26.6972 26.6972 32.6667 19.3333 32.6667C11.9695 32.6667 6 26.6972 6 19.3333C6 11.9695 11.9695 6 19.3333 6C26.6972 6 32.6667 11.9695 32.6667 19.3333Z"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          {/* Page Title */}
          <div className="d-flex justify-content-between info">
            <div className="info-header">
              <h2>التقييمات</h2>
              <h3>{totalItems}</h3>
            </div>
            <div className="print-icon-holder">
              <img
                src={printIcon}
                alt="printIcon"
                className="print-icon"
                onClick={handlePrint}
              />
            </div>
          </div>

          {/* Filters */}
          <div className="ratingsReport-header">
            <div className="ratingsReport-header-right">
              {/* Stars */}
              <div className="star-filter">
                <select
                  onChange={(e) => setSelectedStar(e.target.value)}
                  value={selectedStar}
                  disabled={loading}
                >
                  <option value="">عدد النجوم</option>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <option key={star} value={star}>
                      {star} نجمة
                    </option>
                  ))}
                </select>
              </div>

              {/* Technician */}
              <div className="tech-filter">
                <select
                  value={selectedTechnician}
                  onChange={(e) => setSelectedTechnician(e.target.value)}
                  disabled={loading}
                >
                  <option value="">تحديد الفني</option>
                  {technicians.map((tech) => (
                    <option key={tech.TechnicianId} value={tech.Username}>
                      {tech.Username}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Clear Filters */}
            <div className="ratingsReport-header-left">
              <button
                className="button"
                disabled={
                  (!searchQuery && !selectedStar && !selectedTechnician) ||
                  loading
                }
                onClick={handleClearFilters}
              >
                حذف البحث
              </button>
            </div>
          </div>

          {/* Pagination Controls */}
          <div className="paginateComponent">
            <div className="items-per-page-selector">
              {/* <label htmlFor="itemsPerPage" className="form-label">
                عرض:
              </label> */}
              <select
                id="itemsPerPage"
                className="form-select form-select-sm"
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                style={{
                  width: "80px",
                  display: "inline-block",
                  margin: "0 10px",
                }}
              >
                {itemsPerPageOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              {/* <span>عناصر في كل صفحة</span> */}
            </div>

            {pageCount > 0 && renderPagination()}
          </div>

          {/* Ratings List */}
          <div className="ratingsReport-data">
            {loading && initialLoad ? (
              <div className="loading-overlay">
                <div className="small-spinner"></div>
              </div>
            ) : ratings.length === 0 ? (
              <p className="no-results-message">لا توجد نتائج مطابقة</p>
            ) : (
              ratings.map((rating) => (
                <div className="rate" key={rating.Id}>
                  <div className="rate-right">
                    <div className="rate-info">
                      <div className="rate-info-right">
                        <h5 className="rate-name">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="32"
                            height="33"
                            viewBox="0 0 32 33"
                            fill="none"
                          >
                            <path
                              d="M16.161 17.54C16.0677 17.5267 15.9477 17.5267 15.841 17.54C13.4944 17.46 11.6277 15.54 11.6277 13.18C11.6277 10.7666 13.5744 8.80664 16.001 8.80664C18.4144 8.80664 20.3744 10.7666 20.3744 13.18C20.361 15.54 18.5077 17.46 16.161 17.54Z"
                              stroke="#0D9AE1"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M24.9863 26.3401C22.613 28.5134 19.4663 29.8334 15.9996 29.8334C12.5329 29.8334 9.38627 28.5134 7.01294 26.3401C7.14627 25.0867 7.94627 23.8601 9.37294 22.9001C13.0263 20.4734 18.9996 20.4734 22.6263 22.9001C24.053 23.8601 24.853 25.0867 24.9863 26.3401Z"
                              stroke="#0D9AE1"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M16.0001 29.8334C23.3638 29.8334 29.3334 23.8638 29.3334 16.5001C29.3334 9.13628 23.3638 3.16675 16.0001 3.16675C8.63628 3.16675 2.66675 9.13628 2.66675 16.5001C2.66675 23.8638 8.63628 29.8334 16.0001 29.8334Z"
                              stroke="#0D9AE1"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          {rating.CustomerName}
                        </h5>
                        <h5 className="rate-mobile-number">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="32"
                            height="33"
                            viewBox="0 0 32 33"
                            fill="none"
                          >
                            <path
                              d="M4.6084 3.46484C5.61968 2.43452 7.35818 2.51478 8.35254 3.73438L8.44629 3.85547L10.6182 6.82812C11.3575 7.84004 11.3082 9.17092 10.5938 10.0518L10.4424 10.2217L9.94824 10.7246L10.6621 11.4248C9.97025 10.7458 9.94785 10.7263 9.94629 10.7266L9.93164 10.7412C9.92575 10.7475 9.91982 10.7552 9.91309 10.7627C9.8997 10.7776 9.88439 10.7946 9.86816 10.8145C9.83552 10.8544 9.79792 10.9052 9.75781 10.9658C9.67743 11.0874 9.58802 11.2505 9.51172 11.458C9.35662 11.8798 9.26748 12.4498 9.37695 13.1729C9.57883 14.5061 10.4439 16.3083 12.7021 18.7197L13.1729 19.21C15.781 21.8674 17.7055 22.8632 19.1162 23.085C19.8353 23.198 20.4047 23.1062 20.8262 22.9453C21.0331 22.8663 21.1959 22.7741 21.3164 22.6914C21.3763 22.6503 21.4255 22.6115 21.4648 22.5781C21.4844 22.5615 21.5019 22.5459 21.5166 22.5322C21.5239 22.5254 21.5310 22.5187 21.5371 22.5127L21.5518 22.498C21.5527 22.4971 21.5535 22.4954 20.8398 21.7949L21.5537 22.4961L22.3379 21.6973V21.6963C23.0479 20.9728 24.1435 20.7734 25.1084 21.2139L25.2998 21.3105L28.5898 23.1436C30.1096 23.99 30.4344 25.9978 29.3955 27.2002L29.29 27.3135L26.8438 29.8066C26.3983 30.2605 25.9096 30.5558 25.4004 30.6602L25.1807 30.6934C22.7019 30.9301 16.4803 30.7231 9.66797 24.1631L9.00684 23.5078C2.70513 17.0872 1.30986 11.5961 1.04297 8.82227L1.00391 8.30469C0.968068 7.65859 1.21832 7.02182 1.68945 6.45703L1.90527 6.21875L4.6084 3.46484Z"
                              stroke="#0D9AE1"
                              strokeWidth="2"
                            />
                          </svg>
                          {rating.CustomerPhone}
                        </h5>
                      </div>
                      <div className="rate-info-left">
                        {renderStars(rating.RatingValue)}
                      </div>
                    </div>
                    <div className="rate-comment">
                      <p>{rating.Comments}</p>
                    </div>

                    {(isValidImageUrl(rating.photo) ||
                      isValidImageUrl(rating.photo2)) && (
                      <div className="rate-pics">
                        {isValidImageUrl(rating.photo) && (
                          <div className="image-box">
                            <img
                              src={rating.photo}
                              alt="صورة 1"
                              loading="lazy"
                              onClick={() => openImagePopup(rating.photo)}
                            />
                          </div>
                        )}

                        {isValidImageUrl(rating.photo2) && (
                          <div className="image-box">
                            <img
                              src={rating.photo2}
                              alt="صورة 2"
                              loading="lazy"
                              onClick={() => openImagePopup(rating.photo2)}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="rate-left">
                    <div className="tech-name">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="27"
                        height="27"
                        viewBox="0 0 27 27"
                        fill="none"
                      >
                        <g transform="translate(3.5, 0)">
                          <path
                            d="M10 13.8171C11.5913 13.8171 15.3326 11.8634 15.3326 7.18221C15.3326 6.52918 15.317 5.89842 15.2642 5.30232C15.1776 4.12955 14.9422 3.09016 14.3559 2.2936C13.6039 1.20952 12.2881 0.547607 10 0.547607C7.71243 0.547607 6.39667 1.20932 5.6446 2.29299C5.05803 3.08975 4.82242 4.12942 4.73581 5.30259C4.68305 5.89856 4.66748 6.52932 4.66748 7.18221C4.66748 11.8634 8.40876 13.8171 10 13.8171ZM11.7406 2.97826C11.5854 3.37869 11.5075 3.57894 11.3521 3.9793C10.349 3.69016 9.65097 3.69016 8.6478 3.9793C8.4924 3.57894 8.4147 3.37869 8.25923 2.97826C9.51384 2.59081 10.4862 2.59088 11.7406 2.97826ZM5.46084 6.16731C5.52225 6.30444 5.78325 6.83144 6.18725 6.88083C6.65167 6.93769 7.42254 5.203 10 5.19876C12.5775 5.203 13.3484 6.93769 13.8128 6.88083C14.2168 6.83137 14.4778 6.30437 14.5392 6.16731C14.5518 6.50321 14.5563 6.84322 14.5563 7.18228C14.5563 9.47048 13.5659 10.8882 12.735 11.6746C11.6881 12.6656 10.5258 13.0252 10 13.0252C9.4742 13.0252 8.31193 12.6656 7.26503 11.6746C6.4342 10.8882 5.44376 9.47048 5.44376 7.18228C5.44376 6.84322 5.44831 6.50321 5.46084 6.16731Z"
                            fill="#0D9AE1"
                          />
                          <path
                            d="M19.9 20.6214C19.6278 18.8945 19.0673 16.67 17.9431 15.8794C17.1754 15.3395 14.4969 13.8796 13.3531 13.2562L13.3338 13.2457C13.203 13.1745 13.0434 13.1887 12.9268 13.2821C12.3277 13.7619 11.6714 14.0853 10.9761 14.2433C10.8533 14.2712 10.7525 14.3602 10.7079 14.4802L10.327 15.5045H9.67298L9.29207 14.4802C9.24741 14.3602 9.14663 14.2712 9.02381 14.2433C8.3286 14.0853 7.67231 13.7619 7.07313 13.2821C6.95651 13.1887 6.79689 13.1744 6.66616 13.2457C5.53601 13.8618 2.82482 15.3514 2.06015 15.8771C0.766029 16.7665 0.200229 19.9852 0.0999711 20.6214C0.0900112 20.6845 0.0957498 20.7491 0.116659 20.8094C0.14799 20.8996 0.702312 22.3621 3.7338 23.2994V23.6107C3.7338 23.8009 3.88505 23.9551 4.07145 23.9551L8.24754 23.9586C8.1672 24.4279 7.99557 25.2008 7.65179 25.9645C7.60371 26.071 7.61208 26.1951 7.67442 26.2939C7.73648 26.3928 7.8436 26.4523 7.95857 26.4523H12.0411C12.1561 26.4523 12.2633 26.3927 12.3253 26.2939C12.3873 26.1951 12.3959 26.071 12.3481 25.9645C12.0044 25.2009 11.8324 24.4279 11.752 23.9586L15.9284 23.9551C16.1148 23.9551 16.2661 23.8009 16.2661 23.6107V23.2994C19.2976 22.3621 19.8519 20.8996 19.8832 20.8094C19.9042 20.7491 19.9099 20.6845 19.9 20.6214ZM15.1711 23.0008H14.581V22.882C14.581 22.7062 14.4413 22.5635 14.2688 22.5635C14.0964 22.5635 13.9567 22.7061 13.9567 22.882V23.0008H13.5629V22.882C13.5629 22.7062 13.4232 22.5635 13.2508 22.5635C13.0785 22.5635 12.9386 22.7061 12.9386 22.882V23.0008H4.8286V16.4587H15.1711V23.0008Z"
                            fill="#0D9AE1"
                          />
                          <path
                            d="M8.67611 20.1359C8.43787 20.1359 8.21413 20.2307 8.046 20.4027C7.81647 20.6376 7.73085 20.9814 7.82253 21.3C7.82788 21.3185 7.84239 21.3328 7.86079 21.3376C7.87913 21.3422 7.89859 21.3368 7.91198 21.3232L8.47223 20.7499C8.50587 20.7637 8.59063 20.8112 8.74735 20.9707C8.9073 21.1334 8.95288 21.2199 8.96568 21.2498L8.40443 21.8241C8.39104 21.8378 8.38576 21.8577 8.39051 21.8764C8.39526 21.8951 8.40931 21.9099 8.42751 21.9152C8.50831 21.9392 8.59202 21.9513 8.67638 21.9513C8.91456 21.9513 9.13829 21.8566 9.30636 21.6845C9.57013 21.4146 9.63193 21.0164 9.49322 20.6857L10.0173 20.1511L10.239 20.3772L10.0878 20.5315C10.0549 20.565 10.0368 20.6096 10.0368 20.657C10.0368 20.7045 10.0549 20.7491 10.0878 20.7826L11.1556 21.8719C11.1884 21.9054 11.2322 21.9239 11.2787 21.9239C11.3252 21.9239 11.3689 21.9054 11.4018 21.8719L11.9803 21.2818C12.0132 21.2482 12.0313 21.2036 12.0313 21.1562C12.0313 21.1087 12.0131 21.0641 11.9803 21.0305L10.9125 19.9412C10.8796 19.9077 10.8358 19.8892 10.7893 19.8892C10.7428 19.8892 10.6992 19.9077 10.6662 19.9412L10.5151 20.0954L10.2934 19.8693L10.973 19.1759C11.0826 19.224 11.2014 19.2497 11.3243 19.2497C11.5625 19.2497 11.7862 19.155 11.9544 18.9829C12.1838 18.748 12.2695 18.4042 12.178 18.0856C12.1726 18.067 12.1581 18.0528 12.1398 18.048C12.1214 18.0432 12.102 18.0487 12.0886 18.0624L11.5282 18.6358C11.4946 18.6219 11.4099 18.5745 11.2531 18.415C11.0931 18.2523 11.0476 18.1657 11.0348 18.1358L11.596 17.5615C11.6094 17.5478 11.6147 17.5279 11.6099 17.5093C11.6052 17.4906 11.5911 17.4758 11.5729 17.4704C11.4921 17.4465 11.4084 17.4343 11.3242 17.4343C11.0859 17.4343 10.8622 17.5291 10.6941 17.7012C10.4304 17.9712 10.3686 18.3693 10.5073 18.7L9.82724 19.3938L9.13104 18.6835C9.13051 18.6825 9.13044 18.6814 9.12991 18.6804L8.81192 18.1242C8.80645 18.1146 8.79893 18.1064 8.79003 18.1001L8.25351 17.7204C8.22376 17.6993 8.18373 17.7031 8.15827 17.729L7.91930 17.9728C7.89377 17.9989 7.89021 18.0398 7.91079 18.0701L8.28313 18.6173C8.28933 18.6264 8.29744 18.6341 8.30681 18.6397L8.85190 18.964C8.85295 18.9646 8.85407 18.9648 8.85513 18.9654L9.55113 19.6755L9.02748 20.2097C8.91779 20.1616 8.79893 20.1359 8.67611 20.1359ZM10.4676 20.6449C10.5056 20.6062 10.5674 20.6062 10.6053 20.645L11.2905 21.3439C11.3285 21.3827 11.3285 21.4456 11.2905 21.4845C11.2524 21.5232 11.1907 21.5232 11.1527 21.4845L10.4676 20.7855C10.4294 20.7467 10.4294 20.6837 10.4676 20.6449ZM10.7775 20.3288C10.8156 20.2899 10.8772 20.2899 10.9154 20.3288L11.6004 21.0276C11.6385 21.0664 11.6385 21.1294 11.6004 21.1682C11.5625 21.207 11.5007 21.207 11.4626 21.1681L10.7775 20.4694C10.7395 20.4304 10.7395 20.3676 10.7775 20.3288Z"
                            fill="#0D9AE1"
                          />
                        </g>
                      </svg>
                      <h5>{rating.TechnicianName}</h5>
                    </div>
                    <div className="tech-name">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="33"
                        viewBox="0 0 32 33"
                        fill="none"
                      >
                        <path
                          d="M4 12.4999V27.1666C4 27.5202 4.14048 27.8593 4.39052 28.1094C4.64057 28.3594 4.97971 28.4999 5.33333 28.4999H26.6667C27.0203 28.4999 27.3594 28.3594 27.6095 28.1094C27.8595 27.8593 28 27.5202 28 27.1666V12.4999M4 12.4999H28M4 12.4999V7.16659C4 6.81296 4.14048 6.47382 4.39052 6.22378C4.64057 5.97373 4.97971 5.83325 5.33333 5.83325H26.6667C27.0203 5.83325 27.3594 5.97373 27.6095 6.22378C27.8595 6.47382 28 6.81296 28 7.16659V12.4999"
                          stroke="#132239"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M21.3334 4.5V8.5M10.6667 4.5V8.5"
                          stroke="#0D9AE1"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M12 20.4999L14.6667 23.1666L20 17.8333"
                          stroke="#0D9AE1"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <h5>{rating.CreatedAt?.slice(0, 10)}</h5>
                    </div>
                    <div className="tech-name">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="33"
                        viewBox="0 0 32 33"
                        fill="none"
                      >
                        <path
                          d="M17.0796 7.50024V16.1397L21.0598 20.1194"
                          stroke="#132239"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M8.92871 20.3337C7.16043 20.3339 5.72676 21.7684 5.72656 23.5369C5.72656 25.3055 7.1603 26.7408 8.92871 26.741C10.6981 26.741 12.1328 25.306 12.1328 23.5369C12.1326 21.7679 10.698 20.3337 8.92871 20.3337ZM8.58984 27.9402L7.83789 27.7478C7.37569 27.6299 6.94406 27.4388 6.55566 27.1863L5.87695 26.7449L5.30371 27.3171L5.1416 27.4783L5.04102 27.3787L5.19043 27.2302L5.77246 26.6472L5.3125 25.9646C5.04421 25.5664 4.84198 25.1139 4.7168 24.6248L4.52441 23.8728H3.50195V23.2029H4.52441L4.7168 22.4509C4.83912 21.9732 5.03523 21.5304 5.29492 21.1414L5.75098 20.4597L5.04199 19.7507L5.14258 19.6501L5.27051 19.7781L5.84863 20.3572L6.53027 19.905C6.92015 19.6465 7.35995 19.451 7.83691 19.3269L8.58496 19.1326V18.1101H9.27441V19.1335L10.0234 19.3279C10.5109 19.4543 10.9614 19.6552 11.3584 19.9216L12.04 20.3796L12.6221 19.7986L12.7168 19.7039L12.8174 19.8044L12.1367 20.4851L12.5791 21.1648C12.8299 21.5498 13.0207 21.9814 13.1416 22.446L13.3359 23.1941H14.3564V23.8806H13.3359L13.1416 24.6287C13.0177 25.1052 12.8211 25.5471 12.5625 25.9373L12.1104 26.6189L12.6895 27.197L12.8164 27.324L12.7158 27.4236L12.5879 27.2957L12.0088 26.7175L11.3271 27.1707C10.9375 27.4296 10.496 27.6253 10.0186 27.7488L9.26855 27.9431V28.9636H8.58984V27.9402Z"
                          stroke="#0D9AE1"
                          strokeWidth="2"
                        />
                        <path
                          d="M2.51602 15.936V17.065C2.5087 16.8771 2.50195 16.6892 2.50195 16.5002C2.50195 16.3112 2.5087 16.1233 2.51602 15.936Z"
                          fill="#132239"
                        />
                        <path
                          d="M2.51709 15.8874C2.83828 8.71613 8.75128 3 16.0019 3C16.5155 3 17.0206 3.036 17.5184 3.09281C17.5342 3.0945 17.5505 3.09619 17.5657 3.09787C17.7676 3.1215 17.9678 3.15019 18.167 3.18281C18.2795 3.20081 18.392 3.21994 18.5039 3.24075C18.6322 3.26494 18.7593 3.29025 18.8858 3.31838C19.0912 3.36225 19.2959 3.41288 19.4995 3.46688C19.5423 3.47813 19.5845 3.48881 19.6261 3.50063C20.498 3.74138 21.3592 4.06875 22.1962 4.49906C28.8226 7.90925 31.432 16.0435 28.0272 22.6728C28.025 22.6771 28.0195 22.6788 28.0152 22.6765V22.6765C28.0109 22.6742 28.0055 22.6759 28.0032 22.6803C25.7603 27.0263 21.23 30 16.0019 30"
                          stroke="#132239"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                      <h5>{rating.Request.MaintenanceStartDate}</h5>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default RatingsReport;
